import type { Id, Project } from '../core/model';
import type { ProjectQueries } from '../state/api';

/**
 * Sources that are on program regardless of the scene: audio-only sources whose
 * settings set `alwaysOnProgram`, OBS's "global audio device" behaviour. They are
 * mixed at full gain in every scene and through every transition while they
 * exist in the project.
 */
export function alwaysOnProgramSources(project: Project): Set<Id> {
  const out = new Set<Id>();
  for (const [id, source] of Object.entries(project.sources)) {
    if (source.settings.alwaysOnProgram === true) out.add(id);
  }
  return out;
}

/**
 * Program-mix gain per source for a (possibly in-flight) transition.
 * Sources audible in both scenes stay at 1; sources only in the outgoing scene
 * follow 1 - progress and sources only in the incoming one follow progress.
 * Hidden items do not contribute audio. Always-on-program sources stay at 1.
 */
export function computeProgramMix(
  project: Project,
  queries: ProjectQueries,
  fromSceneId: Id | null,
  toSceneId: Id | null,
  progress: number,
): Record<Id, number> {
  const p = Math.min(1, Math.max(0, progress));
  const always = alwaysOnProgramSources(project);
  const audible = (sceneId: Id | null): Set<Id> => {
    const set = sceneId && project.scenes[sceneId] ? queries.sourcesInScene(project, sceneId, { visibleOnly: true }) : new Set<Id>();
    for (const id of always) set.add(id);
    return set;
  };

  const from = audible(fromSceneId);
  const to = toSceneId === null ? from : audible(toSceneId);
  const gains: Record<Id, number> = {};
  for (const id of from) gains[id] = to.has(id) ? 1 : 1 - p;
  for (const id of to) if (!from.has(id)) gains[id] = p;
  return gains;
}
